<template>
  <div class="round-table">
    <!-- Unpaired player list -->
    <!-- <div class="unpaired-players">
      <div class="is-flex is-align-items-center m-2">
        <p class="is-size-5 has-text-left has-text-info mr-2">
          UNPAIRED PLAYERS:
          {{ unpairedPlayers.length }}/{{ sortedPlayers.length }}
        </p>

        <PlayersProgressBar
          class="is-flex-grow-1"
          :playersStats="playersStats"
        />
      </div>

      <PlayerTags
        :players="unpairedPlayers"
        :roundNumber="round.roundNumber"
        :totalRounds="tournament.totalRounds"
        :canClose="false"
        :hideAbsent="true"
        v-on="$listeners"
      />
    </div> -->

    <!-- Games Table -->
    <div class="table-header">
      <div class="table-cell">#</div>
      <div class="table-cell is-flex is-justify-content-center">
        <span class="image is-24x24">
          <img :src="'KING_WHITE' | chessPieceSvg" alt="White Player" />
        </span>
        <span>&nbsp;White Player</span>
      </div>
      <div class="table-cell pl-0">Result</div>
      <div class="table-cell is-flex is-justify-content-center">
        <span class="image is-24x24">
          <img :src="'KING_BLACK' | chessPieceSvg" alt="Black Player" />
        </span>
        <span>&nbsp;Black Player</span>
      </div>
      <div class="table-cell pr-6">Status</div>
    </div>

    <RecycleScroller
      ref="scroller"
      class="scroller-table"
      :items="scrollerItems"
      :item-size="71"
      key-field="id"
      :key="`round-${round.roundNumber}-games`"
      v-slot="{ item }"
    >
      <TournamentGameRow
        v-if="item.type === 'game'"
        :key="`game-${item.game.board}-${item.game.whiteUsername || 'empty'}-${
          item.game.blackUsername || 'empty'
        }-${item.game.whiteResult || 'null'}-${
          item.game.blackResult || 'null'
        }`"
        :game="item.game"
        :saved-game="
          savedRoundGames && savedRoundGames[roundGames.indexOf(item.game)]
        "
        :tournament="tournament"
        :round="round"
        :round-games="roundGames"
        :all-games="allGames"
        :available-unpaired-players="availableUnpairedPlayers"
        :hover-id="hoverId"
        :find-player="findPlayer"
        :is-offline-class-tournament="isOfflineClassTournament"
        :is-selected="selectedGameId === item.game.gameId"
        @hover="hoverId = $event"
        @rematch="rematchGame"
        @start-game="startGame"
        @set-players="setGamePlayers"
        @set-result="setGameResult"
        @game-selected="selectGame"
      />
      <div
        v-else-if="item.type === 'add-board'"
        class="add-board-row"
        @click="addBoard"
      >
        <span>+ Add board</span>
      </div>
    </RecycleScroller>
  </div>
</template>

<script>
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { randomUUID } from "crypto";
import GameHelper from "@/utils/game-helper";
import gameUrls from "@/mixins/gameUrls";
import { memoize } from "lodash";
import {
  getScoreBeforeGame,
  assignGamePlayers,
} from "vue-chess-shared/tournament/utils/gameUtils";
import {
  isPlayerInRound,
  getColorPref,
} from "vue-chess-shared/tournament/utils/playerUtils";
import { RecycleScroller } from "vue-virtual-scroller";
import TournamentGameRow from "@/components/tournaments/TournamentGameRow.vue";
import {
  hasResultMismatch,
  initGame,
} from "vue-chess-shared/tournament/utils/gameUtils";
import { TOURNAMENT_GAME_RESULT } from "vue-chess-shared/constants";

export default {
  components: {
    RecycleScroller,
    TournamentGameRow,
  },
  name: "TournamentRoundTable",

  props: [
    "tournament",
    "players",
    "round",
    "roundGames",
    "savedRoundGames",
    "allGames",
  ],

  mixins: [gameUrls],

  data() {
    return {
      hoverId: null,
      selectedGameId: null,
    };
  },

  computed: {
    isOfflineClassTournament() {
      // Check if this is an offline class tournament by looking at the route
      // Class tournaments are accessed via /teacher/class-tournaments/:id
      return this.$route.name === "OfflineClassTournament";
    },

    /**
     * Items for the RecycleScroller including games and add-board button
     */
    scrollerItems() {
      const gameItems = (this.roundGames || []).map((game, index) => ({
        id: `game-${game.gameId}`,
        type: "game",
        game: game,
        index: index,
      }));

      // Add the "Add board" button as the last item
      gameItems.push({
        id: "add-board",
        type: "add-board",
      });

      return gameItems;
    },

    /**
     * Memoized sorted players with tournament-specific data
     */
    sortedPlayers() {
      return this.memoizedSortPlayers(this.players, this.round);
    },

    /**
     * Memoized unpaired players calculation
     */
    unpairedPlayers() {
      if (!this.roundGames) return [];
      return this.memoizedGetUnpairedPlayers(
        this.roundGames,
        this.sortedPlayers
      );
    },

    /**
     * Available unpaired players (participating only)
     */
    availableUnpairedPlayers() {
      return this.unpairedPlayers.filter((p) => p.isParticipating);
    },

    /**
     * Player statistics for progress bar
     */
    playersStats() {
      if (!this.sortedPlayers) return null;
      return {
        total: this.sortedPlayers.length,
        absent: this.sortedPlayers.filter((p) => !p.isParticipating).length,
        paired: this.sortedPlayers.length - this.unpairedPlayers.length,
        available: this.availableUnpairedPlayers.length,
      };
    },
  },

  mounted() {
    // Add keyboard event listener for shortcuts
    document.addEventListener("keydown", this.handleKeyboardShortcut);
  },

  beforeDestroy() {
    // Remove keyboard event listener
    document.removeEventListener("keydown", this.handleKeyboardShortcut);
  },

  filters: {
    matchResultText(match) {
      if (!match) return "*no match*";
      if (!match.result && match.status)
        return "*" + match.status.toUpperCase() + "*";
      return (
        GameHelper.scoreText(match.player_1_score, match.player_2_score) +
        "|" +
        match.result
      );
    },
  },

  methods: {
    /**
     * Find player by username
     * @param {string} username - Player username
     * @returns {object|undefined} - Player object
     */
    findPlayer(username) {
      return this.sortedPlayers.find((p) => p.username === username);
    },

    /**
     * Get color preference for a player
     * @param {object} player - Player object
     * @returns {string} - Color preference string
     */
    getColorPref(player) {
      return getColorPref(this.round, player);
    },

    /**
     * Select a game row
     * @param {string} gameId - Game ID to select
     */
    selectGame(gameId) {
      if (this.selectedGameId === gameId) {
        this.selectedGameId = null;
      } else {
        this.selectedGameId = gameId;
      }
    },

    /**
     * Handle keyboard shortcuts for setting game results
     * @param {KeyboardEvent} event - Keyboard event
     */
    handleKeyboardShortcut(event) {
      if (!this.selectedGameId) return;

      const selectedGame = this.roundGames.find(
        (game) => game.gameId === this.selectedGameId
      );
      if (!selectedGame) return;

      // Only handle shortcuts if both players are assigned
      if (!selectedGame.whiteUsername || !selectedGame.blackUsername) return;

      let whiteResult = null;
      let blackResult = null;

      switch (event.key) {
        case "1":
          // White wins
          whiteResult = TOURNAMENT_GAME_RESULT.WIN;
          blackResult = TOURNAMENT_GAME_RESULT.LOSS;
          break;
        case "0":
          // White loses (Black wins)
          whiteResult = TOURNAMENT_GAME_RESULT.LOSS;
          blackResult = TOURNAMENT_GAME_RESULT.WIN;
          break;
        case "=":
          // Draw
          whiteResult = TOURNAMENT_GAME_RESULT.DRAW;
          blackResult = TOURNAMENT_GAME_RESULT.DRAW;
          break;
        case "Backspace":
        case "Delete":
          whiteResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
          blackResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
          break;
        case "Escape":
          this.selectedGameId = null;
          return;

        default:
          return; // Don't prevent default for other keys
      }

      event.preventDefault();
      this.setGameResult(selectedGame, whiteResult, blackResult);

      // Auto-select next row
      this.selectNextGame();
    },

    /**
     * Select the next game row after setting a result
     */
    selectNextGame() {
      if (!this.selectedGameId) return;

      const currentIndex = this.roundGames.findIndex(
        (game) => game.gameId === this.selectedGameId
      );

      if (currentIndex === -1) return;

      // Find the next game with both players assigned
      let nextIndex = currentIndex + 1;
      while (nextIndex < this.roundGames.length) {
        const nextGame = this.roundGames[nextIndex];
        if (nextGame.whiteUsername && nextGame.blackUsername) {
          this.selectedGameId = nextGame.gameId;
          // Auto scroll to the newly selected game
          this.$nextTick(() => {
            this.scrollToIndex(nextIndex);
          });
          return;
        }
        nextIndex++;
      }

      // If no next game found, wrap around to the beginning
      nextIndex = 0;
      while (nextIndex < currentIndex) {
        const nextGame = this.roundGames[nextIndex];
        if (nextGame.whiteUsername && nextGame.blackUsername) {
          this.selectedGameId = nextGame.gameId;
          // Auto scroll to the newly selected game
          this.$nextTick(() => {
            this.scrollToIndex(nextIndex);
          });
          return;
        }
        nextIndex++;
      }

      // If no suitable game found, deselect
      this.selectedGameId = null;
    },

    /**
     * Scroll to the currently selected game in the RecycleScroller
     */
    scrollToIndex(selectedIndex) {
      if (!(selectedIndex >= 0) || !this.$refs.scroller) return;

      if (this.$refs.scroller.scrollToPosition) {
        const position = (selectedIndex - 5) * this.$refs.scroller.itemSize;
        this.$refs.scroller.scrollToPosition(position);
      }
    },

    /**
     * Set game result
     * @param {object} game - Tournament game object
     * @param {number|null} whiteResult - White player result
     * @param {number|null} blackResult - Black player result
     */
    setGameResult(game, whiteResult, blackResult) {
      game.whiteResult = whiteResult;
      game.blackResult = blackResult;
      this.$emit("onRoundGamesUpdated", this.roundGames);
    },

    /**
     * Set game players
     * @param {object} game - Tournament game object
     * @param {string|null} whiteUsername - White player username
     * @param {string|null} blackUsername - Black player username
     */
    setGamePlayers(game, whiteUsername, blackUsername) {
      assignGamePlayers(game, whiteUsername, blackUsername);

      this.$emit("onRoundGamesUpdated", this.roundGames);
    },

    addBoard() {
      const newGame = initGame(
        this.round.roundNumber,
        this.roundGames.length + 1,
        !this.isOfflineClassTournament
      );
      // Create a new array with the added game instead of mutating the prop
      const updatedRoundGames = [...this.roundGames, newGame];
      this.$emit("onRoundGamesUpdated", updatedRoundGames);
    },

    startGame(game) {
      this.$emit("onStartGame", game);
    },

    rematchGame(game) {
      this.$buefy.dialog.confirm({
        title: "Rematch Confirmation",
        message:
          "Are you sure you want to do a <b>rematch</b>? This would create a new match for the 2 players and overwrite the existing one.",
        confirmText: "Rematch",
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          game.matchRef = null;
          game.matchGUID = this.isOfflineClassTournament ? null : randomUUID();
          this.setGamePlayers(game, game.whiteUsername, game.blackUsername);
          this.$emit("onRematchGame", game);
        },
      });
    },

    hasPlayerMismatch(game) {
      return GameHelper.hasPlayerMismatch(game);
    },

    hasResultMismatch(game) {
      if (!game) return false;
      return hasResultMismatch(game, game.matchRef);
    },
  },

  beforeCreate() {
    // Memoize expensive functions
    this.memoizedSortPlayers = memoize((players, round) => {
      let sorted = (players ?? []).map((p) => ({
        ...p,
        isParticipating: isPlayerInRound(p, round.roundNumber),
        scoreBeforeGame: getScoreBeforeGame(p.username, round),
        colorPref: getColorPref(round, p),
      }));
      return sorted.sort((a, b) => b.scoreBeforeGame - a.scoreBeforeGame);
    });

    this.memoizedGetUnpairedPlayers = memoize((games, sortedPlayers) => {
      const pairedUsernames = new Set(
        (games ?? [])
          .flatMap((g) => [g.whiteUsername, g.blackUsername])
          .filter(Boolean)
      );
      return sortedPlayers.filter((p) => !pairedUsernames.has(p.username));
    });
  },
};
</script>

<style scoped>
.unpaired-players {
  border-bottom: 2px solid #ccc;
  padding-bottom: 0.5rem;
}

.scroller-table {
  max-height: 600px; /* Adjust based on your needs */
  width: 100%;
}

.table-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #dbdbdb;
}

.table-header {
  display: flex;
  align-items: center;
  border-bottom: 2px solid #dbdbdb;
  background-color: #5d095d;
  color: white !important;
  height: 3rem;
}

.table-cell {
  padding: 0.5em 0.75em;
}

.add-board-row {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70px;
  border: 4px dashed #ccc;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #666;
  font-weight: 500;
}

.add-board-row:hover {
  border-color: #007bff;
  background-color: #e3f2fd;
  color: #007bff;
}

/* Match the original table column widths */
.table-header .table-cell:nth-child(1) {
  width: 5%;
} /* Board */
.table-header .table-cell:nth-child(2) {
  width: 35%;
} /* White */
.table-header .table-cell:nth-child(3) {
  width: 10%;
} /* Swap */
.table-header .table-cell:nth-child(4) {
  width: 35%;
} /* Black */
.table-header .table-cell:nth-child(5) {
  width: 15%;
} /* Status */
</style>
